import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuthStore } from "@/store/auth";
import { bgTasksApi } from "../api/bgTasks";
import type { TaskListParams, TaskUser } from "../api/bgTasks";
import type { TaskStatus } from "../types";
import { Activity, TaskRow, STATUS_OPTIONS, inputCls } from "./TaskRow";

interface BgTasksViewProps {
  title: string;
  /** Privileged /dev view: list every user's tasks (?all=1) and show the
   *  "triggered by" user dropdown. Off → only the current user's tasks. */
  showUserFilter?: boolean;
}

function Pagination({
  page,
  totalPages,
  onChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  disabled: boolean;
}) {
  if (totalPages <= 1) return null;
  const btnCls =
    "flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300";
  return (
    <div className="flex items-center justify-center gap-4 pt-2">
      <button
        onClick={() => onChange(page - 1)}
        disabled={disabled || page <= 1}
        className={btnCls}
      >
        <ChevronLeft size={14} />
        Poprzednia
      </button>
      <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
        Strona {page} z {totalPages}
      </span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={disabled || page >= totalPages}
        className={btnCls}
      >
        Następna
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

export function BgTasksView({ title, showUserFilter = false }: BgTasksViewProps) {
  // `?task=<id>` (link z panelu zadania w kreatorach, np. sekwencja masowa WPW)
  // prefiltruje listę do tego wpisu i rozwija go od razu — deweloper ląduje wprost
  // na logu workera zamiast szukać wpisu po nazwie.
  const [searchParams] = useSearchParams();
  const linkedTaskId = searchParams.get("task") ?? "";
  const [taskName, setTaskName] = useState("");
  const [taskId, setTaskId] = useState(linkedTaskId);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [triggeredBy, setTriggeredBy] = useState("");
  // System tasks (e.g. nav-badge refresh) are excluded from the list by default;
  // this developer-only opt-in flips the list to show ONLY them.
  const [systemOnly, setSystemOnly] = useState(false);
  const [page, setPage] = useState(1);
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  const showSystemToggle = showUserFilter && isDeveloper;

  const { data: userOptions = [] } = useQuery<TaskUser[]>({
    queryKey: ["bg-tasks-users"],
    queryFn: () => bgTasksApi.users(),
    enabled: showUserFilter,
  });

  const params: TaskListParams = {
    ...(taskName && { task_name: taskName }),
    ...(taskId.trim() && { task_id: taskId.trim() }),
    ...(statusFilter && { status: statusFilter }),
    ...(dateFrom && { date_from: dateFrom }),
    ...(dateTo && { date_to: dateTo }),
    ...(showUserFilter && { all: "1" as const }),
    ...(showUserFilter && triggeredBy && { triggered_by: triggeredBy }),
    ...(showSystemToggle && systemOnly && { system: "1" as const }),
  };

  // Reset to the first page whenever the active filters change.
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    setPage(1);
  }, [paramsKey]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["bg-tasks", params, page],
    queryFn: () => bgTasksApi.list({ ...params, page }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const tasks = query.state.data?.data ?? [];
      return tasks.some((t) => t.status === "pending" || t.status === "running")
        ? 3000
        : false;
    },
  });

  const tasks = data?.data ?? [];
  const meta = data?.meta;
  const totalPages = meta?.total_pages ?? 1;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <Activity size={22} className="text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-0.5" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Nazwa zadania..."
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          className={inputCls}
        />
        <input
          type="text"
          placeholder="ID zadania..."
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
          className={inputCls}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TaskStatus | "")}
          className={inputCls}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {showUserFilter && (
          <select
            value={triggeredBy}
            onChange={(e) => setTriggeredBy(e.target.value)}
            className={inputCls}
          >
            <option value="">Wszyscy użytkownicy</option>
            {userOptions.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.name}
              </option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-2 sm:col-span-1">
          <label className="text-xs text-gray-500 whitespace-nowrap">Data od:</label>
          <DatePicker
            value={dateFrom}
            onChange={setDateFrom}
          />
        </div>
        <div className="flex items-center gap-2 sm:col-span-1">
          <label className="text-xs text-gray-500 whitespace-nowrap">Data do:</label>
          <DatePicker
            value={dateTo}
            onChange={setDateTo}
          />
        </div>
      </div>

      {showSystemToggle && (
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none w-fit">
          <input
            type="checkbox"
            checked={systemOnly}
            onChange={(e) => setSystemOnly(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
          />
          Tylko zadania systemowe
        </label>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[var(--color-primary)]" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">Brak zadań</div>
      ) : (
        <>
          {meta && (
            <p className="text-xs text-gray-400">
              Pokazano {tasks.length} z {meta.count} zadań
            </p>
          )}
          <div className="space-y-2">
            {tasks.map((task) => (
              <TaskRow key={task.id} task={task} defaultExpanded={task.id === linkedTaskId} />
            ))}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            disabled={isFetching}
          />
        </>
      )}
    </div>
  );
}
